import { useEffect, useState } from 'react';
import { api, type PlatformAccount } from '../api.ts';
import { limit, message, overLimit } from './Platform.tsx';

const STATE_LABELS: Readonly<Record<string, string>> = {
  trial: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  suspended: 'Suspended',
  closed: 'Closed',
};

interface AccountsProps {
  onOpen: (accountId: string) => void;
}

/**
 * Every client, with what hangs off them and what they are paying for.
 *
 * Two things are deliberately visible on the list rather than one level in,
 * because both are money and neither is discoverable by looking at a healthy
 * screen: an account with **no package**, which is unlimited by design and
 * therefore unpriced revenue, and an account **over** a limit, which has
 * stopped being assessed and does not know it unless somebody looks.
 */
export const Accounts = ({ onOpen }: AccountsProps) => {
  const [accounts, setAccounts] = useState<PlatformAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');

  const load = () => {
    void api
      .platformAccounts()
      .then((response) => setAccounts(response.accounts))
      .catch((cause: unknown) => setError(message(cause)));
  };

  useEffect(load, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      await api.createAccount({
        name: name.trim(),
        billingEmail: billingEmail.trim() === '' ? null : billingEmail.trim(),
      });
      setName('');
      setBillingEmail('');
      load();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setCreating(false);
    }
  };

  if (error !== null) return <p className="error-banner">{error}</p>;
  if (accounts === null) return <p className="note">Loading clients…</p>;

  const unpriced = accounts.filter((account) => account.subscription === null).length;

  return (
    <section className="pf-stack">
      {unpriced > 0 && (
        <div className="callout">
          {unpriced === 1 ? 'One client has' : `${unpriced} clients have`} no package. They are
          unlimited until one is assigned — deliberately, so that an unfinished admin task on our
          side never stops somebody’s evidence accruing. It is revenue nobody has priced.
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Clients</h2>
        </div>
        <div className="card-body">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>State</th>
                  <th>Package</th>
                  <th>Tenants</th>
                  <th>Operators</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const tenantsOver = overLimit(
                    account.tenantCount,
                    account.subscription?.maxTenants ?? null,
                  );
                  const seatsOver = overLimit(
                    account.userCount,
                    account.subscription?.maxSeats ?? null,
                  );
                  return (
                    <tr key={account.id}>
                      <td>
                        <button type="button" className="link" onClick={() => onOpen(account.id)}>
                          {account.name}
                        </button>
                        {account.billingEmail !== null && (
                          <span className="hint"> {account.billingEmail}</span>
                        )}
                      </td>
                      <td>
                        <span className={`pf-state pf-state-${account.state}`}>
                          {STATE_LABELS[account.state] ?? account.state}
                        </span>
                      </td>
                      <td>{account.subscription?.packageName ?? <em>No package</em>}</td>
                      <td className={tenantsOver ? 'pf-over' : undefined}>
                        {account.tenantCount} / {limit(account.subscription?.maxTenants ?? null)}
                      </td>
                      <td className={seatsOver ? 'pf-over' : undefined}>
                        {account.userCount} / {limit(account.subscription?.maxSeats ?? null)}
                      </td>
                      <td>{account.createdAt.slice(0, 10)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Add a client</h2>
        </div>
        <div className="card-body">
          <p className="lead">
            Creates the account only. Its first operator is invited from inside it, and the account
            starts on trial with no package — which is unlimited until one is assigned.
          </p>
          <div className="td-fields">
            <div className="field">
              <label htmlFor="pf-name">Name</label>
              <input id="pf-name" value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pf-billing">Billing email</label>
              <input
                id="pf-billing"
                value={billingEmail}
                onChange={(event) => setBillingEmail(event.target.value)}
              />
              <span className="hint">
                Optional, and not an operator login. The person who pays is frequently not the
                person who signs in.
              </span>
            </div>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={creating || name.trim() === ''}
              onClick={() => void create()}
            >
              {creating ? 'Creating…' : 'Create client'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
